import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UploadFile from "components/uploadFile";
import useUploadFiles from "pages/signup/hooks/useUploadFiles";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import { ReactComponent as DeleteIcon } from "images/documents/blackDeleteIcon.svg";
import useStartJourney from "pages/signup/hooks/useStartJourney";
import { resumeType } from "utils/constantValues";

function EditCvInformation({
  onDiscard = () => {},
  onSave = () => {},
  setFileName = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});

  const resumeFile = userDetails?.userFiles?.find(
    (file) => file.type === resumeType
  );
  const defaultsValues = {
    resume: resumeFile || "",
  };

  const {
    register,
    handleSubmit,
    // watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });
  const { setTempFileId } = useStartJourney({
    setValue,
  });

  const {
    resumeRef,
    uploadResume,
    isUploading,
    resumeFileName,
    removeResumeFile,
  } = useUploadFiles({ defaultResumeFile: resumeFile });
  useEffect(() => {
    if (resumeFileName) {
      setFileName(resumeFileName);
    }
  }, [resumeFileName, setFileName]);
  const isDisableAllFields = false;

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("CV")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-[20px] p-[24px] self-stretch">
        {/* ************** Upload file ***************/}

        <UploadFile
          name={"resume"}
          register={register}
          registerArgs={{
            required: resumeFileName
              ? false
              : t("errorField", { field: t("resume") }),
          }}
          label={t("yourCvLabel")}
          placeHolderLabel={t("attachUrCv")}
          icon={<AttachIcon />}
          isUploading={isUploading.resume}
          getFile={(e, isDrag) => {
            uploadResume(
              e,
              isDrag,
              setValue,
              setTempFileId,
              isDisableAllFields
            );
          }}
          containerClassName="flex flex-col gap-[10px] pointer-events-auto w-1/2"
          labelClassName="text-primary font-semibold text-[16px]"
          placeHolderContainerClassName={`flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[43px] w-full min-w-[190px] sm-max:min-w-full sm-max:w-full border-[1px] border-dashed ${
            errors.resume && !resumeFileName ? "border-red" : "border-dark-gray"
          } cursor-pointer`}
          isRequired={true}
          errorMessage={errors.resume ? errors.resume.message : ""}
          ref={resumeRef}
          fileName={resumeFileName}
          onRemoveFile={(fileName) => {
            setTempFileId(null);
            removeResumeFile(fileName, setValue);
          }}
          accept=".pdf,.doc,.docx"
          extraText={t("AcceptFormat")}
          customTrashIcon={<DeleteIcon />}
        />
      </div>
    </form>
  );
}

EditCvInformation.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
  cvData: PropTypes.object,
};

export default EditCvInformation;
